/*
 * Industry content resolver — single entry point for screens, SEO meta and the
 * build-time prerender matrix (Story 4.8, i18n-architecture §4.3).
 *
 * `industriesFor(locale)` picks the per-locale file with a fallback to
 * `SOURCE_LOCALE` (en). `industrySlugs(locale)` (consumed by
 * react-router.config.ts + src/seo/meta.ts) derives the prerender/hreflang set
 * from per-locale RECORD PRESENCE — so the matrix, hreflang and sitemap stay
 * consistent by construction (G5). Pure data/functions only (NFR10): zero React.
 */
import { SOURCE_LOCALE, LIVE_LOCALES } from "../../i18n/locales";
import type { Industry } from "./types";
// CMS overlay (DIT-264 / S8 → CMS), generated at build by scripts/fetch-cms-content.mjs.
// Statically imported so it resolves in both the Vite render bundle and Node
// (vite.config.ts build matrix); empty `{}` when there's no CMS → baked fallback.
import { CMS_ARTICLES } from "../_generated/cms-articles";
import { INDUSTRIES_EN } from "./en";
import { INDUSTRIES_NL } from "./nl";
import { INDUSTRIES_FR_BE } from "./fr-be";
import { INDUSTRIES_ES } from "./es";
import { INDUSTRIES_DE } from "./de";
import { INDUSTRIES_IT } from "./it";
import { INDUSTRIES_PT } from "./pt";
import { INDUSTRIES_PL } from "./pl";
import { INDUSTRIES_RO } from "./ro";
import { INDUSTRIES_NO } from "./no";
import { INDUSTRIES_BG } from "./bg";
import { INDUSTRIES_TR } from "./tr";
import { INDUSTRIES_LT } from "./lt";

export type {
  Industry,
  IndustryPain,
  IndustryFeature,
  IndustryStep,
  IndustryStat,
  IndustryCompliance,
} from "./types";

/** Locale code → that locale's industry set. `fr-be` added in Story 4.10 (E4.5). */
const REGISTRY: Record<string, Industry[]> = {
  en: INDUSTRIES_EN,
  // Belgian English (belgium market) ships on the English source across all
  // content — no own catalogs yet — so it reuses the English industry set,
  // matching the i18n `fallbackLng: en`. Swap to a Belgian-English set if authored.
  "en-be": INDUSTRIES_EN,
  nl: INDUSTRIES_NL,
  "fr-be": INDUSTRIES_FR_BE,
  es: INDUSTRIES_ES,
  de: INDUSTRIES_DE,
  it: INDUSTRIES_IT,
  pt: INDUSTRIES_PT,
  pl: INDUSTRIES_PL,
  ro: INDUSTRIES_RO,
  no: INDUSTRIES_NO,
  bg: INDUSTRIES_BG,
  tr: INDUSTRIES_TR,
  lt: INDUSTRIES_LT,
};

// Build-time guard (G5): a live locale with no industry set would silently fall
// back to English (see `industriesFor`) and ship wrong-language pages with HTTP
// 200. Fail the build loudly instead — promoting `fr-be` to `live` in E4.5
// without adding it here throws at module load (prerender imports this barrel).
for (const { code } of LIVE_LOCALES) {
  if (!REGISTRY[code]) {
    throw new Error(
      `[content/industries] live locale "${code}" has no industry set in REGISTRY — add it (or keep the locale 'draft').`,
    );
  }
}

/**
 * Resolve the industry set for a locale: the CMS entry wins per slug, and any
 * baked industry the CMS doesn't carry is kept.
 *
 * This used to return the CMS set wholesale whenever it was non-empty, which
 * made shipping an industry in code alone impossible: the CMS held the original
 * nine, so the six non-technical ones were dropped and `/industry/retail` fell
 * through to construction — a shop page silently rendering a building site.
 * Merging keeps editors in control of what they've authored without letting a
 * stale CMS hide new content. Order follows the baked catalog, with any
 * CMS-only additions appended.
 */
export const industriesFor = (locale?: string): Industry[] => {
  const baked = (locale && REGISTRY[locale]) || REGISTRY[SOURCE_LOCALE];
  const cms = locale ? (CMS_ARTICLES.industries[locale] as Industry[] | undefined) : undefined;
  if (!cms || !cms.length) return baked;
  const bySlug = new Map(cms.map((i) => [i.slug, i]));
  const merged = baked.map((i) => bySlug.get(i.slug) ?? i);
  const bakedSlugs = new Set(baked.map((i) => i.slug));
  return [...merged, ...cms.filter((i) => !bakedSlugs.has(i.slug))];
};

/** slug → Industry map for a locale (replaces the old global `INDUSTRY_MAP`). */
export const industryMapFor = (locale?: string): Record<string, Industry> =>
  Object.fromEntries(industriesFor(locale).map((i) => [i.slug, i]));

/**
 * Slug list that drives the prerender matrix (react-router.config.ts) and the
 * hreflang set (src/seo/meta.ts). Derived from per-locale presence: every live
 * locale lists the industries its own file carries.
 */
export const industrySlugs = (locale?: string): string[] =>
  industriesFor(locale).map((i) => i.slug);
